import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { DollarSign, Send, Clock, CheckCircle, XCircle, FileText, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney, currencyLabel } from '../lib/currency';

const CURRENCY = 'USD';
const fm = (v) => formatMoney(v, CURRENCY);

export default function MySalaryRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ request_type: 'advance', amount: '', message: '' });
  const [file, setFile] = useState(null);

  useEffect(() => { fetchRequests(); }, []);
  const fetchRequests = async () => { try { setRequests((await api.get('/salary-requests')).data); } catch(e){} finally { setLoading(false); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append('request_type', form.request_type);
      fd.append('amount', form.amount);
      fd.append('message', form.message);
      if (file) fd.append('document', file);
      await api.post('/salary-requests', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Demande soumise');
      setOpen(false); setForm({ request_type: 'advance', amount: '', message: '' }); setFile(null);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const typeLabels = { advance: 'Avance sur salaire', credit: 'Credit', loan: 'Pret' };
  const statusBadge = (s) => {
    const m = { pending: { l: 'En attente', c: 'bg-amber-100 text-amber-700' }, approved: { l: 'Approuve', c: 'bg-green-100 text-green-700' }, rejected: { l: 'Refuse', c: 'bg-red-100 text-red-700' } };
    const v = m[s] || m.pending;
    return <Badge variant="outline" className={v.c}>{v.l}</Badge>;
  };

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="my-salary-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Mes Demandes</h1>
          <p className="text-slate-600 mt-1">Avances, credits et prets</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-request-btn"><Send className="w-4 h-4 mr-2" /> Nouvelle demande</Button>
      </div>

      {requests.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Aucune demande</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <Card key={r.id} className="border-slate-200" data-testid={`request-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">{typeLabels[r.request_type] || r.request_type} - <span className="text-green-700">{fm(r.amount)}</span></p>
                      <p className="text-sm text-slate-500 mt-1">{r.message}</p>
                      {r.document_url && (
                        <a href={r.document_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                          <FileText className="w-3 h-3" /> {r.document_filename || 'Document joint'}
                        </a>
                      )}
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  {statusBadge(r.status)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouvelle demande</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Type *</Label>
              <select value={form.request_type} onChange={(e) => setForm({...form, request_type: e.target.value})} className="w-full rounded-md border border-slate-300 p-2" data-testid="req-type-select">
                <option value="advance">Avance sur salaire</option>
                <option value="credit">Credit</option>
                <option value="loan">Pret</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Montant ({currencyLabel(CURRENCY)}) *</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} required data-testid="req-amount" />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required rows={3} placeholder="Decrivez votre demande..." data-testid="req-message" />
            </div>
            <div className="space-y-2">
              <Label>Document (lettre signee)</Label>
              <div className="flex items-center gap-2 p-3 border border-dashed border-slate-300 rounded-lg">
                <Upload className="w-4 h-4 text-slate-400" />
                <input type="file" onChange={(e) => setFile(e.target.files[0])} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="text-sm" data-testid="req-file" />
              </div>
              <p className="text-xs text-slate-500">Joindre la lettre avec signature</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1" data-testid="req-submit">Soumettre</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
